import {html, type HTMLTemplateResult} from "lit";
import type {GimmickName} from "../../../domain/gimmicks/gimmick-registry";
import {presetMs} from "../../../domain/gimmicks/timer.state";

// Side-effect imports: register <o2d-gimmick-stopwatch>, <o2d-gimmick-picker> and <o2d-gimmick-timer>, so a
// gimmick tile never depends on some other part of the app having loaded them first.
import "../../gimmicks/stopwatch/o2d-gimmick-stopwatch.component";
import "../../gimmicks/picker/o2d-gimmick-picker.component";
import "../../gimmicks/timer/o2d-gimmick-timer.component";

/** What a gimmick element gets from its tile. */
export interface GimmickMount {
    /** The tile's id — the key of the viewer's own state. */
    readonly tileId: string;
    /** The options stored with the tile, raw. */
    readonly options: Readonly<Record<string, unknown>>;
}

/**
 * Gimmick → how a `link-tile-gimmick` renders it (feature plans 0071, 0072, 0073). UI side of
 * `domain/gimmicks/gimmick-registry.ts`, kept apart from it for the same reason `tile-registry.ts` is kept
 * apart from `link-target-serializer.ts`: the domain must not import components. `Record<GimmickName, …>`
 * makes a new gimmick name fail to compile until it has a renderer here.
 */
export const GIMMICK_RENDERERS: Readonly<Record<GimmickName, (mount: GimmickMount) => HTMLTemplateResult>> = {
    stopwatch: ({tileId, options}) => html`
        <o2d-gimmick-stopwatch class="dashboard-tile__gimmick-body"
                               .tileId=${tileId}
                               .savedSkin=${typeof options.skin === 'string' ? options.skin : ''}
        ></o2d-gimmick-stopwatch>
    `,
    picker: ({tileId, options}) => html`
        <o2d-gimmick-picker class="dashboard-tile__gimmick-body"
                            .tileId=${tileId}
                            .savedSkin=${typeof options.skin === 'string' ? options.skin : ''}
        ></o2d-gimmick-picker>
    `,
    timer: ({tileId, options}) => html`
        <o2d-gimmick-timer class="dashboard-tile__gimmick-body"
                           .tileId=${tileId}
                           .savedSkin=${typeof options.skin === 'string' ? options.skin : ''}
                           .presetMs=${presetMs(options)}
        ></o2d-gimmick-timer>
    `,
};
